import { Box, Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel } from '@mui/material';
import React, { useEffect, useState } from 'react'
import { GridModel, TableOrderType } from '../../core/models/GridModels';
import { visuallyHidden } from '@mui/utils';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../../core/consts';
import { handleToggleCheckIds, renderTablePaginationInfo } from '../../core/utility';
import { DataTableCol } from './Models';

interface PaginationEventModel {
  pageNumber: number;
  pageSize: number;
}

interface SortEventModel {
  orderBy: string;
  order: boolean;
}

type SortProps<T> = | {
  disableSort: true;
  orderByOption?: never;
  orderOption?: never;
  sortEvent?: never;
} | {
  disableSort?: undefined | false;
  orderByOption: keyof T;
  orderOption: TableOrderType;
  sortEvent: (evet: SortEventModel) => void;
}

type PaginationProps = | {
  disablePagination: true;
  pageNumber?: never;
  pageSizeOptions?: never;
  paginationEvent?: never;
} | {
  disablePagination?: undefined | false;
  pageNumber: number;
  pageSizeOptions?: number[];
  paginationEvent: (event: PaginationEventModel) => void;
}

type BatchProps<T> = | {
  isCheckboxActive: true;
  batchField: keyof T;
  batchValueEvent: (items: number[]) => void;
} | {
  isCheckboxActive?: undefined | false;
  batchField?: never;
  batchValueEvent?: never;
}

interface CommonProps<T> {
  data: GridModel<T>;
  cells: DataTableCol<T>[];
  // orderByOption: keyof T;
  // orderOption: TableOrderType;
  // pageNumber: number;
  // pageSizeOptions?: number[];
  // isCheckboxActive?: boolean;
  // batchField?: keyof T;
  // disablePagination?: boolean;
  // paginationEvent?: (event: PaginationEventModel) => void;
  // sortEvent?: (evet: SortEventModel) => void;
  //batchValueEvent?: (items: number[]) => void;
};

type DataTableProps<T> = CommonProps<T> & SortProps<T> & PaginationProps & BatchProps<T>;

const DataTable = <T extends {}>(props: DataTableProps<T>) => {

  const { data, cells,
    orderByOption, orderOption, pageNumber, pageSizeOptions, disablePagination, disableSort,
    isCheckboxActive, batchField,
    paginationEvent, sortEvent, batchValueEvent } = props;

  //states
  const [tableData, setTableData] = useState<T[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState(pageSizeOptions ? pageSizeOptions[0] : DEFAULT_PAGE_SIZE);
  const [order, setOrder] = React.useState<TableOrderType>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof T | undefined>(orderByOption);
  const [selectedRows, setSelectedRows] = React.useState<number[]>([]);

  useEffect(() => {
    setTableData(data.items);
  }, [data.items])

  useEffect(() => {
    setOrderBy(orderByOption)
  }, [orderByOption])

  useEffect(() => {
    if (orderOption)
      setOrder(orderOption)
  }, [orderOption])

  useEffect(() => {
    if (pageNumber)
      setPage(pageNumber)
  }, [pageNumber])

  //methods

  let handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof T) => { };
  let handleChangePage = (event: unknown, newPage: number) => { };
  let handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => { };
  let handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => { };
  let handleCheckRow = (rowNum: number) => { };

  if ((disableSort === undefined || disableSort === false) && typeof sortEvent === "function") {

    handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof T) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
      sortEvent({
        orderBy: property as string,
        order: isAsc ? true : false
      });
    };
  }

  if ((disablePagination === undefined || disablePagination === false) && typeof paginationEvent === "function") {

    handleChangePage = (event: unknown, newPage: number) => {
      setPage(newPage);
      paginationEvent({
        pageNumber: newPage,
        pageSize: rowsPerPage
      });
    };

    handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      const pageSize = parseInt(event.target.value, 10);
      setRowsPerPage(pageSize);
      setPage(0);
      paginationEvent({
        pageNumber: 0,
        pageSize: pageSize
      });
    };
  }

  if (isCheckboxActive && batchField && typeof batchValueEvent === "function") {

    handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        const newSelected = tableData.map((n) => Number(n[batchField]));
        setSelectedRows(newSelected);
        batchValueEvent(newSelected);
        return;
      }
      setSelectedRows([]);
      batchValueEvent([]);
    };

    handleCheckRow = (rowNum: number) => {
      const newSelected = handleToggleCheckIds(rowNum, selectedRows);
      setSelectedRows(newSelected);
      batchValueEvent(newSelected);
    }
  }

  return (
    <>
      <TableContainer>
        <Table className='ks-table'>

          <TableHead>
            <TableRow>

              {isCheckboxActive && (
                <TableCell padding="checkbox">
                  <Checkbox
                    className='checkall'
                    indeterminate={selectedRows.length > 0 && selectedRows.length < tableData.length}
                    checked={tableData.length > 0 && selectedRows.length === tableData.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
              )}

              {cells.map(cell => {

                if (cell.sortable === true) {
                  return (
                    <TableCell
                      key={cell.id}
                      align={cell.align ? cell.align : 'left'}
                      sortDirection={orderBy === cell.id ? order : false}
                      width={cell.width}>
                      <TableSortLabel
                        className='ks-table-sort-label'
                        active={orderBy === cell.id}
                        direction={orderBy === cell.id ? order : 'asc'}
                        onClick={(e: any) => handleRequestSort(e, cell.id as keyof T)}
                      >
                        {cell.label}
                        {orderBy === cell.id ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                  );
                }

                return (<TableCell key={cell.id} align={cell.align ? cell.align : 'left'} width={cell.width}>{cell.label}</TableCell>)
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {tableData.map((row: T, index) => (
              <TableRow key={index} hover>

                {isCheckboxActive && batchField && (
                  <TableCell
                    padding="checkbox"
                    role="checkbox"
                    onClick={() => handleCheckRow(Number(row[batchField]))}>
                    <Checkbox
                      color="primary"
                      checked={selectedRows.indexOf(Number(row[batchField])) !== -1}
                    />
                  </TableCell>
                )}

                {cells.map(cell => {
                  const selectedValue = row[cell.id as keyof T] as string;

                  return (
                    <TableCell
                      component="td"
                      scope="row"
                      key={cell.id}
                      align={cell.align ? cell.align : 'left'}>
                      {
                        cell.renderCell !== undefined ?
                          cell.renderCell(row, cell.id, selectedValue) :
                          <div style={{ width: cell.width ? `${cell.width}px` : '' }}>{selectedValue}</div>
                      }
                    </TableCell>
                  )
                })}

              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {(disablePagination === undefined || !disablePagination) && (
        <TablePagination
          rowsPerPageOptions={pageSizeOptions ?? PAGE_SIZE_OPTIONS}
          colSpan={3}
          count={props.data.totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          component="div"
          labelRowsPerPage="Sayfa başına kayıt:"
          labelDisplayedRows={renderTablePaginationInfo}
        />
      )}
    </>
  )
};

export default React.memo(DataTable) as typeof DataTable;